import { motion } from 'framer-motion'
import PageHeader from '@shared/components/PageHeader'
import SEO from '@shared/components/SEO'
import { BOARD_OF_TRUSTEES } from './data/wp-structured'
import type { Person } from './data/wp-structured'

function initials(name: string) {
    return name
        .replace(/^(Prof\.|Prof|Dr\.|Dr|Mr\.|Mr|Ms\.|Ms|Mrs\.|Mrs)\s+/i, '')
        .split(/[\s.]+/)
        .filter(Boolean)
        .slice(0, 2)
        .map((p) => p[0])
        .join('')
        .toUpperCase()
}

function Avatar({ person, size }: { person: Person; size: number }) {
    return (
        <div
            className="overflow-hidden flex items-center justify-center relative shrink-0"
            style={{
                width: size,
                height: size,
                background: 'var(--color-paper-100)',
                border: '1px solid var(--color-rule-strong)',
                borderRadius: '50%'
            }}
        >
            <span
                aria-hidden
                className="absolute inset-0 flex items-center justify-center font-display"
                style={{ fontSize: size * 0.32, color: 'var(--color-ink-400)' }}
            >
                {initials(person.name)}
            </span>
            {person.photo && (
                <img
                    src={person.photo}
                    alt={person.name}
                    loading="lazy"
                    className="relative w-full h-full object-cover"
                    style={{ objectPosition: 'center 22%' }}
                    onError={(e) => {
                        ;(e.currentTarget as HTMLImageElement).style.display = 'none'
                    }}
                />
            )}
        </div>
    )
}

function roleLabel(person: Person) {
    return person.role || person.affiliation
}

function Eyebrow({ children }: { children: React.ReactNode }) {
    return (
        <div
            className="text-[10px] uppercase font-semibold"
            style={{ letterSpacing: '0.22em', color: 'var(--color-gold-700)' }}
        >
            {children}
        </div>
    )
}

function EmailLink({ email }: { email: string }) {
    return (
        <a
            href={`mailto:${email}`}
            className="inline-block text-[11.5px]"
            style={{ color: 'var(--color-gold-700)', letterSpacing: '0.02em' }}
        >
            {email}
        </a>
    )
}

function FeaturedTrustee({ person }: { person: Person }) {
    return (
        <section className="section">
            <div className="container-prose">
                <motion.div
                    initial={{ opacity: 0, y: 12 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5 }}
                    className="flex flex-col items-center text-center sm:flex-row sm:text-left sm:items-center gap-8 sm:gap-12 px-7 py-10 sm:px-12 sm:py-14"
                    style={{
                        border: '1px solid var(--color-rule)',
                        borderTop: '3px solid var(--color-gold-600)',
                        background: 'var(--color-paper-50)'
                    }}
                >
                    <Avatar person={person} size={168} />
                    <div className="flex-1">
                        <Eyebrow>Leadership</Eyebrow>
                        <h2
                            className="font-display mt-3"
                            style={{
                                fontSize: 'clamp(1.6rem, 3vw, 2.1rem)',
                                lineHeight: 1.08,
                                letterSpacing: '-0.02em',
                                color: 'var(--color-ink-700)',
                                fontWeight: 500
                            }}
                        >
                            {person.name}
                        </h2>
                        <div
                            className="mt-3 text-[13px] uppercase font-semibold"
                            style={{ letterSpacing: '0.18em', color: 'var(--color-ink-500)' }}
                        >
                            {roleLabel(person)}
                        </div>
                        <hr
                            className="mt-5 mb-5 mx-auto sm:mx-0"
                            style={{ width: '3rem', border: 0, borderTop: '2px solid var(--color-gold-500)' }}
                        />
                        <p className="prose-body" style={{ maxWidth: '34rem' }}>
                            As Managing Trustee, {person.name} anchors the Centre's
                            governance — chairing the Board's stewardship of its scholarly
                            mission, finances, and long-term institutional direction.
                        </p>
                        {person.email && (
                            <div className="mt-4">
                                <EmailLink email={person.email} />
                            </div>
                        )}
                    </div>
                </motion.div>
            </div>
        </section>
    )
}

function TrusteeCard({ person, index }: { person: Person; index: number }) {
    return (
        <motion.figure
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: (index % 4) * 0.06 }}
            className="flex flex-col items-center text-center px-8 py-10 w-full sm:w-72"
            style={{
                border: '1px solid var(--color-rule)',
                borderTop: '3px solid var(--color-gold-500)',
                background: 'var(--color-paper-50)'
            }}
        >
            <Avatar person={person} size={112} />
            <figcaption className="mt-6 flex flex-col items-center">
                <div
                    className="font-display"
                    style={{
                        fontSize: '1.2rem',
                        color: 'var(--color-ink-700)',
                        letterSpacing: '-0.012em',
                        lineHeight: 1.2,
                        fontWeight: 500
                    }}
                >
                    {person.name}
                </div>
                {roleLabel(person) && (
                    <div
                        className="mt-3 inline-block px-3 py-1 text-[10px] uppercase font-semibold"
                        style={{
                            letterSpacing: '0.2em',
                            color: 'var(--color-gold-700)',
                            background: 'var(--color-paper-100)',
                            border: '1px solid var(--color-rule)'
                        }}
                    >
                        {roleLabel(person)}
                    </div>
                )}
                {person.email && (
                    <div className="mt-3">
                        <EmailLink email={person.email} />
                    </div>
                )}
            </figcaption>
        </motion.figure>
    )
}

export default function BoardTrustees() {
    const [lead, ...rest] = BOARD_OF_TRUSTEES

    return (
        <>
            <SEO
                title="Board of Trustees"
                description="The trustees who steward the governance and scholarly mission of the Centre for Study of Politics and Governance."
                path="/board-of-trustees"
            />
            <PageHeader
                eyebrow="The Centre"
                title="Board of Trustees"
                lede="The trustees who safeguard the Centre's governance, finances, and enduring commitment to rigorous scholarship."
            />

            <section className="section">
                <div className="container-prose text-center">
                    <motion.div
                        initial={{ opacity: 0, y: 12 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.5 }}
                    >
                        <div className="eyebrow eyebrow--center">Governance &amp; Stewardship</div>
                        <p className="lede mt-5 mx-auto" style={{ maxWidth: '44rem' }}>
                            The Board of Trustees holds ultimate responsibility for the Centre's
                            governance, ensuring that its resources and reputation are stewarded
                            in service of independent, India-rooted scholarship. Its members set
                            the institution's strategic direction and uphold the standards of
                            integrity on which its work depends.
                        </p>
                    </motion.div>
                </div>
            </section>

            {lead && <FeaturedTrustee person={lead} />}

            {rest.length > 0 && (
                <section className="section" style={{ borderTop: '1px solid var(--color-rule)' }}>
                    <div className="container-prose">
                        <motion.div
                            initial={{ opacity: 0, y: 12 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.5 }}
                            className="text-center mb-12"
                        >
                            <div className="eyebrow eyebrow--center">The Board</div>
                            <h2 className="section-title mt-5">Trustees</h2>
                        </motion.div>

                        <ul className="flex flex-wrap justify-center gap-6 max-w-4xl mx-auto">
                            {rest.map((p, i) => (
                                <li key={`${p.name}-${i}`} className="flex">
                                    <TrusteeCard person={p} index={i} />
                                </li>
                            ))}
                        </ul>
                    </div>
                </section>
            )}
        </>
    )
}
